import React from "react";
import { Loader2 } from "lucide-react";

import {
  BROKER_FIELD_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { DashboardLayoutDialogFooter } from "@/views/Dashboard/Dashboard/components/DashboardLayoutDialogFooter";

export const REMOVE_BROKER_CONFIRM_PHRASE = "remove broker";

export interface RemoveBrokerDialogProps {
  open: boolean;
  brokerDisplayName: string;
  accountCount: number;
  confirmText: string;
  onConfirmTextChange: (value: string) => void;
  isRemoving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function RemoveBrokerDialog({
  open,
  brokerDisplayName,
  accountCount,
  confirmText,
  onConfirmTextChange,
  isRemoving,
  onCancel,
  onConfirm,
}: RemoveBrokerDialogProps) {
  const confirmMatches =
    confirmText.trim().toLowerCase() === REMOVE_BROKER_CONFIRM_PHRASE;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isRemoving) onCancel();
  };

  const accountsLabel =
    accountCount === 1 ? "1 connected account" : `${accountCount} connected accounts`;

  return (
    <StrategyPineDetailDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Remove broker"
      showHeader={false}
      showDismissButton={false}
      contentClassName="sm:max-w-md"
      bodyClassName="flex-none overflow-visible"
      footer={
        <DashboardLayoutDialogFooter onCancel={onCancel}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            data-trade-log-action="delete"
            className="shrink-0 border-destructive/40 text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive focus-visible:border-destructive/60 focus-visible:ring-destructive/25 dark:border-destructive/50 dark:hover:bg-destructive/15 [&_svg]:text-current"
            disabled={!confirmMatches || isRemoving}
            aria-busy={isRemoving || undefined}
            onClick={onConfirm}
          >
            {isRemoving ? (
              <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            ) : (
              <span className="normal-case tracking-normal">Remove broker</span>
            )}
          </Button>
        </DashboardLayoutDialogFooter>
      }
    >
      {open ? (
        <Field className="gap-2">
          <FieldLabel htmlFor="broker-remove-broker-confirm" className={BROKER_ALERT_TITLE_CLASS}>
            Remove Broker Completely
          </FieldLabel>
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            This permanently deletes Tessera data for{" "}
            <span className="font-medium text-[#6b7280]">{brokerDisplayName}</span>
            {accountCount > 0 ? (
              <>
                {" "}
                ({accountsLabel}), including trades, milestones, layouts, and reports
              </>
            ) : null}
            , and removes saved API credentials. It does not close your brokerage account. If this
            stops partway, run Remove broker again. Enter &lsquo;remove broker&rsquo; to confirm.
          </FieldDescription>
          <Input
            id="broker-remove-broker-confirm"
            value={confirmText}
            onChange={(event) => onConfirmTextChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape" && !isRemoving) onCancel();
            }}
            placeholder="Type 'remove broker' to confirm"
            autoFocus
            disabled={isRemoving}
            autoComplete="off"
            data-font="primary"
          />
        </Field>
      ) : null}
    </StrategyPineDetailDialog>
  );
}
